<template>
  <DataTable
    v-model:expanded-rows="expandedRows"
    v-model:editing-rows="editingRows"
    data-sb-regression="sb-data-table"
    :data-key="props.rowKey"
    :value="props.data"
    :loading="props.loading"
    :table-class="$style.dataTable"
    :class="$style.containerTable"
    :row-class="setTopRowClass"
    :removable-sort="!props.apiSorting"
    :lazy="props.apiSorting"
    :sort-field="computedSortingState?.field"
    :sort-order="computedSortingState?.order"
    :scrollable="useStickyHeaders"
    row-hover
    :scroll-height="props.scrollHeight"
    :edit-mode="editMode"
    :row-group-mode="props.rowGroupMode"
    :group-rows-by="props.groupRowsBy"
    :resizable-columns="props.resizableColumns"
    :column-resize-mode="props.columnResizeMode"
    :pt="passthroughOptions"
    @cell-edit-complete="$emit('cell-edit-complete', $event)"
    @row-edit-save="$emit('row-edit-save', { data: $event })"
    @sort="sortViaApi"
  >
    <!-- Action Bar -->
    <div>
      <ColumnGroup
        v-if="displayActionBar"
        type="header"
      >
        <Row>
          <Column
            :hidden="!props.canExpand"
            :frozen="useStickyHeaders"
            :class="{
              [$style.alignFrozenLeft]: useStickyHeaders,
            }"
          >
            <template #header>
              <RowExpansionHeader
                @expand-all="expandAllRows"
                @collapse-all="collapseAllRows"
              />
            </template>
          </Column>
          <Column
            :hidden="!props.selectable"
            :frozen="useStickyHeaders"
            :class="{
              [$style.selectionColumn]: true,
              [$style.alignFrozenLeft]: useStickyHeaders,
            }"
          >
            <template
              v-if="props.selectable === 'multiple-all'"
              #header
            >
              <Checkbox
                :model-value="allRowsSelected"
                :indeterminate="props.indeterminateDefaultSelected && someRowsSelected"
                binary
                :pt:box:class="$style.checkboxBox"
                @update:model-value="toggleAllRowsSelection"
              />
            </template>
          </Column>
          <Column
            :colspan="props.columnDefinitions.length"
          >
            <template #header>
              <slot
                name="sb-table-action-bar"
                :selected-rows="selectedRowData"
                :selected-sub-rows="selectedSubRowData"
                :rows="{
                  flatRows: selectedRowData,
                }"
              />
            </template>
          </Column>
        </Row>
      </ColumnGroup>
    </div>

    <!-- Expander Column -->
    <Column
      expander
      :frozen="useStickyHeaders"
      :hidden="!props.canExpand"
      :class="rowExpansionStyle.expanderColumn"
    >
      <template #header>
        <RowExpansionHeader
          data-sb-automation="expander-column-header"
          @expand-all="expandAllRows"
          @collapse-all="collapseAllRows"
        />
      </template>
      <template #body="slotProps">
        <div :class="rowExpansionStyle.expanderColumnBody">
          <span
            v-if="!rowCanExpand(slotProps.data)"
            :class="rowExpansionStyle.noColumnExpansion"
          />
          <img
            v-else
            :alt="isRowExpanded(slotProps.data) ? $t('sbTable.collapseRow') : $t('sbTable.expandRow')"
            :src="ChevronRightIcon"
            :class="{
              [rowExpansionStyle.expandedRow]: isRowExpanded(slotProps.data),
              [rowExpansionStyle.canExpandRow]: true,
              [rowExpansionStyle.expanderIcon]: true,
            }"
            data-sb-automation="row-expander"
            @click="toggleRowExpansion(slotProps)"
          >
        </div>
      </template>
    </Column>

    <!-- Selection Column -->
    <Column
      :hidden="!props.selectable"
      :frozen="useStickyHeaders"
      :class="{
        [$style.selectionColumn]: true,
        [$style.alignFrozenLeft]: useStickyHeaders,
      }"
    >
      <template
        v-if="props.selectable === 'multiple-all'"
        #header
      >
        <Checkbox
          :model-value="allRowsSelected"
          :indeterminate="props.indeterminateDefaultSelected && someRowsSelected"
          :pt:input="{ 'data-sb-automation': 'select-all-checkbox' }"
          binary
          data-sb-automation="selection-column-header"
          @update:model-value="toggleAllRowsSelection"
        />
      </template>
      <template #body="slotProps">
        <Checkbox
          :disabled="isRowDisabled(slotProps.data)"
          :model-value="isRowSelected(slotProps.data)"
          binary
          data-sb-automation="selection-column"
          @update:model-value="toggleRowSelection({ data: slotProps.data, checked: $event })"
        />
      </template>
    </Column>

    <!-- Dynamic Columns -->
    <Column
      v-for="col of computedColumnDefinitions"
      :key="col.row.field"
      v-memo="[get(props.data, col.row.field)]"
      :field="col.row.field"
      :sortable="col.sortable"
      :frozen="col.frozen"
      :align-frozen="col.alignFrozen"
      :class="{
        [$style.alignFrozenLeft]: col.frozen && col.alignFrozen !== 'right',
        [$style.alignFrozenRight]: col.frozen && col.alignFrozen === 'right'
      }"
      :body-class="col.row.class"
      :header-class="col.header.class"
      :pt="{
        root: {
          'data-sb-automation': col.row.dataSbAutomation,
        },
        columnHeaderContent: {
          style: col.header.style,
        },
        bodycell: {
          style: col.row.style,
        },
      }"
      :style="col.style"
    >
      <template #header>
        <template v-if="col.header.cell">
          <component
            :is="col.header.cell"
            :header="col.header"
            :column="col"
          />
        </template>
        <span
          v-else
          v-tooltip.top="col.header.tooltip"
          class="p-datatable-column-title"
        >
          {{ col.header.name }}
        </span>
      </template>
      <template #sorticon="{ sortOrder }">
        <ColumnSorting :sort-order="sortOrder" />
      </template>
      <template #body="slotProps">
        <BodyCell
          v-tooltip="rowTooltip(slotProps.data, col.row, 'right')"
          v-tooltip.top="rowTooltip(slotProps.data, col.row, 'top')"
          v-tooltip.bottom="rowTooltip(slotProps.data, col.row, 'bottom')"
          v-tooltip.left="rowTooltip(slotProps.data, col.row, 'left')"
          :column="col"
          :data="slotProps.data"
          @row-clicked="(e) => emitRowClickedEvent(e)"
        />
      </template>
      <template
        v-if="col.row.editable"
        #editor="slotProps"
      >
        <slot
          name="editor"
          v-bind="slotProps"
        />
      </template>
    </Column>

    <!-- Row Editor Column -->
    <Column
      v-if="editMode === 'row'"
      :row-editor="true"
      :class="$style.rowEditor"
      :body-class="$style.rowEditorBody"
    />

    <template #empty>
      <slot
        v-if="displayEmptyTableSlot"
        name="sb-table-empty-state"
      />
    </template>
    <template #expansion="slotProps">
      <div
        :class="$style.subRowSlot"
        data-sb-automation="sub-row-slot"
      >
        <slot
          v-if="displaySubRowSlot"
          name="sb-table-subrow"
          v-bind="slotProps"
        />
        <DataTable
          v-else-if="rowCanExpand(slotProps.data)"
          v-model:expanded-rows="expandedSubRows"
          v-model:editing-rows="editingSubRows"
          :data-key="props.subRowKey"
          :value="slotProps.data.subRows"
          :row-class="(rowData) => setSubRowClass(rowData, slotProps.data)"
          :removable-sort="true"
          :edit-mode="subRowEditModeParam"
          :scrollable="props.useStickyHeaders"
          :resizable-columns="props.subRowsTableResizableColumns"
          :column-resize-mode="props.subRowsTableResizableColumnsMode"
          :pt="subTablePassThroughOptions"
          @cell-edit-complete="$emit('cell-edit-complete', $event)"
          @row-edit-save="$emit('row-edit-save', { data: $event, parentData: slotProps.data })"
        >
          <Column
            :hidden="!props.subRowsTableSelectable"
            :class="{
              [$style.selectionColumn]: true,
              [$style.alignFrozenLeft]: useStickyHeaders,
            }"
            :frozen="useStickyHeaders"
          >
            <template
              v-if="props.subRowsTableSelectable === 'multiple-all'"
              #header
            >
              <Checkbox
                :model-value="subRowsSelectedCount(slotProps.data)"
                binary
                @update:model-value="() => { }"
              />
            </template>
            <template #body="slotPropsChild">
              <Checkbox
                :disabled="isRowDisabled(slotPropsChild.data, slotProps.data)"
                :model-value="isSubRowSelected(slotPropsChild.data, slotProps.data)"
                binary
                @update:model-value="toggleSubRowSelection({
                  data: slotPropsChild.data,
                  checked: $event
                }, slotProps.data)"
              />
            </template>
          </Column>

          <Column
            v-for="col of computedSubRowColumnDefinitions"
            :key="col.row.field"
            v-memo="[get(slotProps.data.subRows, col.row.field)]"
            :sortable="false"
            :frozen="col.frozen"
            :align-frozen="col.alignFrozen"
            :class="{
              'align-frozen-left': col.frozen && col.alignFrozen === 'left',
              'align-frozen-right': col.frozen && col.alignFrozen === 'right'
            }"
            :field="col.row.field"
            :pt="{
              columnHeaderContent: {
                style: col.header.style,
              },
              bodycell: {
                style: col.row.style,
                'data-sb-automation': col.row.dataSbAutomation.startsWith('subrow-')
                  ? col.row.dataSbAutomation
                  : `subrow-${col.row.dataSbAutomation}`,
              },
            }"
            :style="col.style"
          >
            <template #header>
              <template v-if="col.header.cell">
                <component
                  :is="col.header.cell"
                  :header="col.header"
                  :column="col"
                />
              </template>
              <span
                v-else
                v-tooltip.top="col.header.tooltip"
                class="p-datatable-column-title"
              >
                {{ col.header.name }}
              </span>
            </template>
            <template #sorticon="{ sortOrder }">
              <ColumnSorting :sort-order="sortOrder" />
            </template>
            <template #body="bodySlotProps">
              <BodyCell
                v-tooltip="rowTooltip(slotProps.data, col.row, 'right')"
                v-tooltip.top="rowTooltip(slotProps.data, col.row, 'top')"
                v-tooltip.bottom="rowTooltip(slotProps.data, col.row, 'bottom')"
                v-tooltip.left="rowTooltip(slotProps.data, col.row, 'left')"
                :column="col"
                :data="bodySlotProps.data"
                @row-clicked="(e) => emitRowClickedEvent(e)"
              />
            </template>
            <template
              v-if="col.row.editable"
              #editor="editorSlotProps"
            >
              <slot
                name="editor"
                :data-sb-automation="'cell-' + col.row.field"
                v-bind="editorSlotProps"
              />
            </template>
          </Column>

          <Column
            v-if="subRowEditModeParam === 'row'"
            :row-editor="true"
            :class="$style.rowEditor"
            :body-class="$style.rowEditorBody"
          />
        </DataTable>
      </div>
    </template>
    <template
      v-if="slots['groupheader']"
      #groupheader="slotProps"
    >
      <slot
        v-bind="slotProps"
        name="groupheader"
      />
    </template>
    <template
      v-if="slots['groupfooter']"
      #groupfooter="slotProps"
    >
      <slot
        v-bind="slotProps"
        name="groupfooter"
      />
    </template>
    <template
      v-if="slots.footer || props.showPagination"
      #footer
    >
      <slot
        v-if="slots.footer"
        name="footer"
      />
      <CursorBasedPagination
        v-else-if="props.pagination.cursors"
        v-bind="props.pagination"
        :current-record-count="props.data.length"
        @fetch-page="($event) => emit('fetch-page', $event)"
      />
      <LegacyBasedPagination
        v-else
        :pagination="props.pagination"
        @fetch-page="($event) => emit('fetch-page', $event)"
      />
    </template>
    <template
      v-if="displayHeaderSlot"
      #header
    >
      <slot
        v-if="slots['sb-toolbar']"
        name="sb-toolbar"
      />
      <slot
        v-else-if="slots['sb-filter']"
        name="sb-filter"
      />
      <slot
        v-else-if="slots.header"
        name="header"
      />
      <div
        v-if="shouldRenderSearchBar"
        :class="$style.filterContainerBar"
      >
        <template v-if="shouldRenderSearchBar">
          <div
            v-if="shouldRenderSearchInput"
            :class="$style.searchBar"
          >
            <sb-search-bar
              v-model:search="search"
              :placeholder="searchProps?.placeholder"
              :min-search-term-length="searchProps?.minSearchTermLength"
              :debounce-time="searchProps?.debounceTime"
              :is-search-on-enter="searchProps?.isSearchOnEnter"
            />
          </div>

          <template v-if="shouldRenderTableFilters">
            <slot
              v-if="slots['custom-filters']"
              name="custom-filters"
              :applied-filters="appliedFilters"
              :applied-filters-callback="newObject => appliedFilters = newObject"
            />
            <sb-table-filters
              v-else
              v-model:appliedFilters="appliedFilters"
              :filter-definitions="filterDefinitions"
            />
          </template>
          <template v-if="shouldRenderTableColumns">
            <slot
              v-if="slots['custom-actions']"
              name="custom-actions"
            />
            <sb-table-columns
              v-else
              v-model:appliedColumns="appliedColumns"
              :column-definitions="columnDefinitions"
            />
          </template>
          <sb-table-filters-selection
            v-if="shouldRenderTableFilters && hasAppliedFilters"
            v-model:applied-filters="appliedFilters"
            v-model:search="search"
            :filter-definitions="filterDefinitions"
            :class="$style.tableFiltersSelection"
            @chip-removed="$emit('chip-removed', $event)"
          />
        </template>
      </div>
    </template>
    <template #loading>
      <sb-loading-spinner data-sb-automation="data-table-loading-spinner" />
    </template>
  </DataTable>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, useSlots, defineOptions, defineModel, useCssModule, useAttrs } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import Checkbox from 'primevue/checkbox';
import ChevronRightIcon from '@public/media/icons/chevron-right.svg';
import { mapToPrimevueColumns } from '@src/components/Table/next/props';
import rowExpansionStyle from '@src/components/Table/next/components/style/rowExpansion.module.scss';
import RowExpansionHeader from '@src/components/Table/next/components/RowExpansionHeader.vue';
import ColumnSorting from '@src/components/Table/next/components/ColumnSorting.vue';
import BodyCell from '@src/components/Table/next/components/BodyCell.vue';
import get from 'lodash/get';
import SbLoadingSpinner from '@src/components/Loading/SbLoadingSpinner.vue';
import CursorBasedPagination from '@src/components/Table/next/components/pagination/CursorBasedPagination.vue';
import { useSearchBar } from '@src/components/Filter/next/composables/useSearchBar';
import SbSearchBar from '@src/components/Filter/next/components/SbSearchBar.vue';
import { useTableFilters } from '@src/components/Filter/next/composables/useTableFilters';
import SbTableFilters from '@src/components/Filter/next/components/SbTableFilters.vue';
import SbTableFiltersSelection from '@src/components/Filter/next/components/SbTableFiltersSelection.vue';
import { useTableColumns } from '@src/components/Filter/next/composables/useTableColumns';
import SbTableColumns from '@src/components/Filter/next/components/SbTableColumns.vue';
import { useSelection } from '@src/components/Table/next/composables/useSelection';
import { useSelectionV2 } from '@src/components/Table/next/composables/useSelectionV2';
import { useExpansion } from '@src/components/Table/next/composables/useExpansion';
import LegacyBasedPagination from '@src/components/Table/next/components/pagination/LegacyBasedPagination.vue';
import { ColumnDefinition } from '@src/components/Table/next/props/columnDefinition';

const $style = useCssModule();
const attrs = useAttrs();

defineOptions({
  name: 'SbDataTable',
});

const props = defineProps({
  /**
   * The data corresponding to the rows of the table
   * To add sub rows to the table add subRows as a property in your data which will contain the data for the sub rows.
   */
  data: {
    type: Array,
    default: () => ([]),
  },
  /**
    * The column definitions required for the table.
    * available column properties. Objects containing any of the following:
    * header,
    * footer,
    * id,
    * cell,
    * class:{
    *    header, //applied to the th for that column
    *    cell, //applied to the td for that column
    *    footer, //applied to the td for that column
    * },
    * size,
    * minSize,
    * maxSize,
    * colSpan,
    * inputType, //Any html5 input type https://www.w3schools.com/html/html_form_input_types.asp
    * inputAttributes, //Attributes to apply to the edit input element
    * columns,
    */
  columnDefinitions: {
    required: true,
    type: Array,
    validator(value) {
      return value.map((col, index) => {
        const column = new ColumnDefinition(col, index);
        return column.isValid();
      }).every((isValid) => isValid);
    },
  },
  /**
    * Same as the column definitions but applicable only to subrows.
    * This allows us to render the first level of subrows as an sb-table.
    * Multiple levels of subrows as tables is not supported
    * Different column definitions can be used for different rows.
    * In this case the subrowColumnDefinitions should be an array of arrays of column definitions.
    * The row object should specify which subrowColumnDefinitions to use by adding a subrowColumnDefinitionsToUse
    * property to the row object. This property should be the index of the subrowColumnDefinitions array.
    */
  subrowColumnDefinitions: {
    required: false,
    type: Array,
    default: () => [],
  },
  /**
    * For SubTables, this flag indicates if a parent row should be overriden as selected/unselected when all or none subrows are selected
    * */
  overrideParentOnOff: {
    type: Boolean,
    default: false,
  },
  /**
    * When selectable is multiple-all this flag will indicate if the header checkbox should have an indeterminate state when some rows are selected.
    */
  indeterminateDefaultSelected: {
    type: Boolean,
    default: false,
  },
  /**
    * Whether the rows can be expanded or not.
    */
  canExpand: {
    type: Boolean,
    default: true,
  },
  /**
    * If sorting logic will be done by an API.
    */
  apiSorting: {
    type: Boolean,
    default: false,
  },
  /**
    * Used to set the type of selection strategy
    */
  selectable: {
    type: String,
    default: null,
    validator: (value) => value.match(/(single|multiple|multiple-all)/),
  },
  /**
   * Used to set the type of selection strategy for the sub table
   */
  subRowsTableSelectable: {
    type: String,
    default: null,
    validator: (value) => value.match(/(single|multiple|multiple-all)/),
  },
  /**
    * The unique identifier for each row.
    * It is required if the table is selectable or expandable.
    */
  rowKey: {
    type: String,
    default: undefined,
  },
  /**
    * The unique identifier for each sub row.
    * It is required if the sub table is selectable or expandable.
    */
  subRowKey: {
    type: String,
    default: undefined,
  },
  /**
   * If the headers should be sticky on overflow
   */
  useStickyHeaders: {
    type: Boolean,
    default: false,
  },
  /**
   * Height of the scrollable table area when use sticky headers is true
   */
  scrollHeight: {
    type: String,
    default: 'flex',
  },
  /**
   * Whether pagination should be displayed or not.
   */
  showPagination: {
    type: Boolean,
    default: true,
  },
  /**
   * Legacy Pagination object with default values
   *  range: 3,
   *  pageSize: 10,
   *  currentPage: 1,
   *  totalPages: 2,
   *  totalRecords: 0,
   *  visibleRecords: 0,
   *  hidePrevious: false,
   *  hideLinks: false,
   *  hideNext: false,
   *  hideText: false,
   *  hideInput: false,
   *
   * Cursor Based Pagination object with default values
   *  pageSize: 10,
   *  pageNumber: 1,
   *  pageSizeOptions: [5, 10, 15, 20, 25, 50, 100],
   *  cursors: {
   *   first: true,
   *   previous: true,
   *   next: true,
   *   last: true,
   *  },
   */
  pagination: {
    type: Object,
    default: () => ({
      pageSize: 10,
      pageNumber: 1,
      range: 3,
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      visibleRecords: 0,
      hidePrevious: false,
      hideLinks: false,
      hideNext: false,
      hideText: false,
      hideInput: false,
    }),
  },
  /**
   * Collection of IDs that should be selected in the table at render
   */
  preselectedKeys: {
    type: Array,
    default: () => [],
  },
  disableSelectedKeys: {
    type: Array,
    default: () => [],
  },
  /**
   * Whether the table is loading or not.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Table edit mode
   */
  editMode: {
    type: String,
    required: false,
    default: null,
    validator: (value) => {
      return !value || value.match(/(cell|row)/);
    },
  },
  /**
   * Sub Row Table edit mode. If not defined, it will be equal to 'editMode' value.
   * Can disable subrow row/cell edit mode with subRowEditMode=false
   */
  subRowEditMode: {
    type: [String, Boolean],
    required: false,
    validator: (value) => {
      return !value || value.match(/(cell|row)/);
    },
    default: () => undefined,
  },

  /**
   * Search props applied to the search bar
   * - minSearchTermLength: Minimum length of the search term before triggering a search
   * - placeholder: Placeholder text for the search input
   * - debounceTime: Debounce time for the search input in milliseconds. Default value: 500ms
   * - isSearchOnEnter: If true, the search will only be triggered when the user presses Enter. Default value: false
   */
  searchProps: {
    type: Object,
    default: () => ({}),
  },
  /**
   *  Whether to show the action bar for the table.
   * Note: Action bar needs to be provided via the slot, there is no default action bar.
   */
  showActionBar: {
    type: Boolean,
    default: false,
  },
  /**
   *  Filter definitions array
   */
  filterDefinitions: {
    type: Array,
    default: () => [],
  },
  /**
   * The row group mode for the table.
   * Can be 'subheader' or 'rowspan'.
   */
  rowGroupMode: {
    type: String,
    default: null,
    validator: (value) => {
      return !value || value.match(/(subheader|rowspan)/);
    },
  },
  /**
   * One or more field names to use in row grouping.
   */
  groupRowsBy: {
    type: [String, Array, Function],
    default: null,
  },
  /**
   * If the table columns are resizable.
   */
  resizableColumns: {
    type: Boolean,
    default: false,
  },
  /**
   * The mode of the column resizing for the table.
   */
  columnResizeMode: {
    type: String,
    default: null,
    validator: (value) => ['fit', 'expand'].includes(value),
  },
  /**
   * If the sub table columns are resizable.
   */
  subRowsTableResizableColumns: {
    type: Boolean,
    default: false,
  },
  /**
   * The mode of the column resizing for the sub table.
   */
  subRowsTableResizableColumnsMode: {
    type: String,
    default: null,
    validator: (value) => ['fit', 'expand'].includes(value),
  },
  selectedRowKeys: {
    type: Object,
    default: null,
  },
  selectedSubRowKeys: {
    type: Object,
    default: null,
  },
});

if ((props.selectable || props.canExpand) && !props.rowKey) {
  throw new Error('rowKey is required when using selection or expandable rows');
}

if (props.subRowsTableSelectable && !props.subRowKey) {
  throw new Error('subRowKey is required when using subRowsTableSelectable');
}

const emit = defineEmits([
  /**
   * Emitted when a row is clicked.
   */
  'row-clicked',
  /**
   * Emitted when sorting is controlled via API.
   */
  'api-sort',
  /**
   * Emitted when the main table is selectable and the checkbox value changes.
   */
  'row-selection-updated',
  /**
   * Emitted when there is a selectable table in the expandable row, and one of those rows are selected.
   */
  'sub-table-row-selection-updated',
  /**
   * Emitted when a pagination button has been clicked.
   * @param {string} type - The type of button clicked.
   */
  'fetch-page',
  /**
   * Emitted when finish editing a cell in edit-mode=cell
   */
  'cell-edit-complete',
  /**
   * Emitted when finish editing a row in edit-mode=row
   */
  'row-edit-save',
  'update:selected-row-keys',
  'update:selected-row-data',
  'update:selected-sub-row-keys',
  'update:selected-sub-row-data',
  /*
  * Emitted when chip is removed from the table filters section
  */
  'chip-removed',
]);
const slots = useSlots();

const search = defineModel('search', {
  type: String,
});
const appliedFilters = defineModel('appliedFilters', {
  type: Object,
});
const appliedColumns = defineModel('appliedColumns', {
  type: Array,
});
const { shouldRenderTableFilters } = useTableFilters(appliedFilters);
const { shouldRenderTableColumns } = useTableColumns(appliedColumns);
const { shouldRenderSearchInput } = useSearchBar(search);

const computedColumnDefinitions = computed(() => {
  return !shouldRenderTableColumns.value
    ? mapToPrimevueColumns(props.columnDefinitions, props)
    : mapToPrimevueColumns(appliedColumns?.value ?? props.columnDefinitions, props);
});

const computedSubRowColumnDefinitions = computed(() => {
  if (!props.subrowColumnDefinitions || props.subrowColumnDefinitions.length === 0) {
    return computedColumnDefinitions.value;
  }
  return mapToPrimevueColumns(props.subrowColumnDefinitions, props);
});

const computedSortingState = computed(() => {
  const sortedColumn = props.apiSorting
    ? computedColumnDefinitions.value.find((column) => column.sortingState)
    : undefined;

  return sortedColumn
    ? { field: sortedColumn.row.field, order: sortedColumn.sortingState === 'asc' ? 1 : -1 }
    : undefined;
});

const {
  expandedRows,
  expandedSubRows,
  rowCanExpand,
  expandRow,
  // eslint-disable-next-line no-unused-vars
  collapseRow,
  expandAllRows,
  collapseAllRows,
  isRowExpanded,
  toggleRowExpansion,
} = useExpansion(props);

const {
  selectedRowData,
  selectedSubRowData,
  toggleAllRowsSelection,
  toggleRowSelection,
  toggleSubRowSelection,
  isRowSelected,
  isRowDisabled,
  isSubRowSelected,
  displayActionBar,
  allRowsSelected,
  someRowsSelected,
} = props.selectable === 'multiple-all' && props.selectedRowKeys
  ? useSelectionV2(props, slots, emit, rowCanExpand)
  : useSelection(props, emit, slots, rowCanExpand, expandRow, expandAllRows);

const editingRows = ref([]);
const editingSubRows = ref([]);

const hasAppliedFilters = computed(() => {
  return !!search.value || Object.keys(appliedFilters.value).length > 0;
});

const displayEmptyTableSlot = computed(() => !props.loading && props.data.length === 0 && slots['sb-table-empty-state']);
const displaySubRowSlot = computed(() => !props.loading && slots['sb-table-subrow']);
const displayHeaderSlot = computed(() => slots['sb-toolbar'] || slots['sb-filter'] || slots.header || shouldRenderSearchInput.value || shouldRenderTableFilters.value || shouldRenderTableColumns.value);
const subRowEditModeParam = computed(() => {
  if (props.subRowEditMode === false) return null;
  return props.subRowEditMode || props.editMode;
});
const shouldRenderSearchBar = computed(() => {
  return shouldRenderSearchInput.value || shouldRenderTableFilters.value || shouldRenderTableColumns.value;
});

const emitRowClickedEvent = (row) => {
  emit('row-clicked', row);
};

const sortViaApi = (sortEvent) => {
  const columnDefinitionIndex = computedColumnDefinitions.value
    .findIndex((col) => col.row.field === sortEvent.sortField);

  if (columnDefinitionIndex === -1 || !computedColumnDefinitions.value[columnDefinitionIndex].sortable) {
    return;
  }

  emit('api-sort', {
    ...props.columnDefinitions[columnDefinitionIndex],
    index: columnDefinitionIndex,
  });
};

const setRowClass = (rowData, parentRow = false) => {
  const classes = [parentRow ? 'datatable-sub-row' : 'datatable-row'];

  if (isRowDisabled(rowData, parentRow)) {
    classes.push($style.disabledRow);
  } else {
    let rowSelected = false;

    if (parentRow) {
      rowSelected = isSubRowSelected(rowData, parentRow);
    } else {
      rowSelected = isRowSelected(rowData);
    }

    if (rowSelected) {
      classes.push('p-datatable-selectable-row');
      classes.push('p-datatable-row-selected');
    }
  }

  return classes;
};
const setTopRowClass = (rowData) => {
  return setRowClass(rowData);
};
const setSubRowClass = (rowData, parentRow) => {
  return setRowClass(rowData, parentRow);
};

const rowTooltip = (rowData, rowDefinition, position) => {
  let tooltip;

  if (rowDefinition.tooltip) {
    if (rowDefinition.tooltipOptions?.position) {
      tooltip = position === rowDefinition.tooltipOptions.position ? rowDefinition.tooltip : undefined;
    } else {
      tooltip = position === 'right' ? rowDefinition.tooltip : undefined;
    }
  } else if (rowDefinition.tooltipOptions) {
    const fieldTooltip = {
      value: get(rowData, rowDefinition.tooltipOptions.field),
    };

    if (rowDefinition.tooltipOptions.position) {
      tooltip = position === rowDefinition.tooltipOptions.position ? fieldTooltip : undefined;
    } else {
      tooltip = position === 'right' ? fieldTooltip : undefined;
    }
  }

  return tooltip;
};

const passthroughOptions = computed(() => {
  return {
    ...(attrs.pt || {}),
    headerRow: displayActionBar && $style.actionBar,
  };
});
const subTablePassThroughOptions = computed(() => {
  return {
    root: {
      style: displayActionBar && 'position: unset',
    },
    table: {
      'data-sb-automation': 'expanded-table',
    },
    thead: {
      style: displayActionBar && 'position: unset',
    },
    bodyrow: {
      'data-sb-automation': 'datatable-subrow',
    },
  };
});
</script>

<style lang="scss" module>
@use '@src/components/Table/next/style/dataTable.module.scss';
</style>
